/**
 * The look of a QR label (v7 CORE-9 part 3; the owner, 2026-10-03): the code's own text size, a frame round the QR
 * (on or off, its size and thickness, its colours, ten small designs), what is behind the QR (plain, a soft glow,
 * a pale tint, or a picture), and text along the frame. ONE function draws a label as SVG in millimetres; the
 * preview, Print and Download SVG all use it, so what is seen is what is printed.
 *
 * The owner, 2026-10-06: the frame is not only a circle — a square, hexagon, octagon, pentagon or diamond, each with
 * rounded corners if wanted; text on the frame's line at the top, at the bottom, or all the way round like a seal
 * ({code} writes the code there); and which parts of the code are written (DSG · DB-BAR · 1111 · 1, any combination).
 *
 * The QR is never recoloured, cut or covered: it is drawn as generated (black on its own white quiet zone) on a white
 * plate, above every decoration.
 */
import type { PrintedLabel, Template } from './LabelEditor';
import desigoLogo from '../../assets/desigo-logo.png?inline';
import rtcomLogo from '../../assets/rtcom-logo.png?inline';

/** The DESIGO wordmark (the owner, 2026-10-25), height over width, as the picture is. */
const LOGO_RATIO = 190 / 900;
const RTCOM_RATIO = 232 / 900;   // the RTCOM logo, trimmed (black ink for a light ground)

export type Design = 'solid' | 'double' | 'dashed' | 'beads' | 'ticks' | 'drops' | 'leaves' | 'stars' | 'sheen' | 'emboss';
export type Backdrop = 'white' | 'glow' | 'tint' | 'image';
export type Shape = 'circle' | 'square' | 'hexagon' | 'octagon' | 'pentagon' | 'diamond';
export type CodePart = 'brand' | 'kind' | 'number' | 'check';
export type FrameTextMode = 'topBottom' | 'around';
export interface LabelStyle {
  codePt?: number; ring?: boolean; ringD?: number; ringW?: number; ringColor?: string; accent?: string; design?: Design;
  bg?: Backdrop; glowColor?: string; codeOnRing?: boolean; imageOpacity?: number;
  /** No white behind the label or round the QR's dark squares (the owner, 2026-10-03): it prints on whatever it is stuck to. */
  transparent?: boolean;
  /** The frame's shape and the rounding of its corners in mm (not for a circle). */
  shape?: Shape; corner?: number;
  /** Text on the frame's line; {code} is replaced by the code as written under the QR. */
  frameTop?: string; frameBottom?: string; frameAround?: string; frameAround2?: string; frameMode?: FrameTextMode; framePt?: number; frameColor?: string;
  /** The colour of the second line round the frame (the owner, 2026-10-26: the legal text in a quiet grey); empty = frameColor. */
  frameColor2?: string;
  /** The code under the QR follows the circle (the owner, 2026-10-26); a circular frame only. */
  curve?: boolean;
  /** Which parts of the code are written, comma-separated (brand,kind,number,check); empty or absent = the whole code. */
  codeParts?: string;
  /** A line above the QR, inside the frame (the owner, 2026-10-25: "DESIGO G+"); {code}, {on}, {class} and {site} are filled in. */
  lineAbove?: string;
  /** The DESIGO logo above the QR instead of a line of words (the owner, 2026-10-25: "the DESIGO logo only, not variant-wise"). */
  logo?: boolean;
  /** Which logo: 'desigo' (default) or 'rtcom'. */
  logoBrand?: 'desigo' | 'rtcom';
  /** The sheet's colour outside the labels, and the colour inside the frame (the owner, 2026-10-25); #rrggbb, empty = as before (white / transparent). */
  sheetColor?: string; insideColor?: string;
}
export const DESIGNS: readonly { id: Design; label: string }[] = [
  { id: 'solid', label: 'Plain line' }, { id: 'double', label: 'Double line' }, { id: 'dashed', label: 'Dashes' },
  { id: 'beads', label: 'Beads' }, { id: 'ticks', label: 'Clock ticks' }, { id: 'drops', label: 'Milk drops' },
  { id: 'leaves', label: 'Leaves' }, { id: 'stars', label: 'Stars' }, { id: 'sheen', label: 'Sheen (two colours)' },
  { id: 'emboss', label: 'Raised 3D' },
];
export const SHAPES: readonly { id: Shape; label: string }[] = [
  { id: 'circle', label: 'Circle' }, { id: 'square', label: 'Square' }, { id: 'hexagon', label: 'Hexagon' },
  { id: 'octagon', label: 'Octagon' }, { id: 'pentagon', label: 'Pentagon' }, { id: 'diamond', label: 'Diamond' },
];
export const CODE_PARTS: readonly { id: CodePart; label: string }[] = [
  { id: 'brand', label: 'Brand' }, { id: 'kind', label: 'Kind' }, { id: 'number', label: 'Number' }, { id: 'check', label: 'Check digit' },
];
export const STYLE_DEFAULTS: Required<Omit<LabelStyle, 'codePt' | 'framePt' | 'frameBottom'>> & { frameBottom: string | undefined } = {
  ring: false, ringD: 40, ringW: 3, ringColor: '#7c3aed', accent: '#14b8a6', design: 'solid', bg: 'white', glowColor: '#c4b5fd',
  codeOnRing: false, imageOpacity: 0.35, transparent: true, shape: 'circle', corner: 0,
  frameTop: '', frameBottom: undefined, frameAround: '', frameAround2: '', frameMode: 'topBottom', frameColor: '#ffffff', frameColor2: '', curve: false, codeParts: '', lineAbove: '', logo: false, logoBrand: 'desigo' as 'desigo' | 'rtcom', sheetColor: '', insideColor: '',
};

const f = (n: number) => n.toFixed(2);
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
const PT = 0.3528;   // a point, in millimetres

/** A printed code split into its parts: DSG-DB-BAR-001111-1 → brand DSG, kind DB-BAR, number 001111, check 1. */
export function codeSplit(payload: string): Record<CodePart, string> | null {
  const s = payload.split('-');
  if (s.length < 4 || s[s.length - 1]!.length !== 1 || !/^\d+$/.test(s[s.length - 2]!)) return null;
  return { brand: s[0]!, kind: s.slice(1, -2).join('-'), number: s[s.length - 2]!, check: s[s.length - 1]! };
}
export const partsOf = (st: Pick<LabelStyle, 'codeParts'>): CodePart[] =>
  (st.codeParts ?? '').split(',').filter((p): p is CodePart => CODE_PARTS.some((c) => c.id === p));
/** The code as written on the label: only the chosen parts, in their order, joined by "-". */
export function codeText(t: Template, payload: string): string {
  const parts = partsOf(t.style ?? {});
  const split = codeSplit(payload);
  if (!parts.length || !split) return payload;
  return CODE_PARTS.filter((c) => parts.includes(c.id)).map((c) => split[c.id]).filter(Boolean).join('-');
}

export const styleOf = (t: Template) => {
  const s = { ...STYLE_DEFAULTS, codePt: t.textPt, ...(t.style ?? {}) };
  // Before 2026-10-06 the only frame text was "the code along the ring", at the bottom.
  const frameBottom = s.frameBottom ?? (s.codeOnRing ? '{code}' : '');
  return { ...s, frameBottom, framePt: s.framePt ?? s.codePt };
};
type Style = ReturnType<typeof styleOf>;
/** The sheet's colour outside the labels: the chosen one, else white, else none when the look is transparent. */
export const sheetFill = (t: Template): string => { const s = styleOf(t); return /^#[0-9a-f]{6}$/i.test(s.sheetColor) ? s.sheetColor : s.transparent ? '' : '#ffffff'; };
const frameTexts = (s: Style) => (s.frameMode === 'around' ? [s.frameAround, s.frameAround2] : [s.frameTop, s.frameBottom]).filter((x) => !!x?.trim());
/** The code is written on the frame, so it is not repeated under the QR. */
const codeOnFrame = (t: Template, s: Style) => s.ring && t.showCode && frameTexts(s).some((x) => x.includes('{code}'));

/** The extra line may be several, one per line break (the owner, 2026-10-26: the website, then the phone, each small under the code). */
const extraLines = (t: Template) => (t.extraLine ?? '').split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
export const textLines = (t: Template, l: PrintedLabel) => [
  t.showCode ? codeText(t, l.payload) : null, t.showOn ? (l.on ?? l.class_name) : null, t.showSite ? l.site : null, ...extraLines(t),
  l.copy > 1 ? `copy ${l.copy}` : null,
].filter((x): x is string => !!x);
/** A line of the label's own words: {code} the code as written, {on} what it is on, {class} the kind of code, {site} the site. */
export const fillTokens = (t: Template, l: PrintedLabel, str: string) => str
  .replace(/\{code\}/g, codeText(t, l.payload)).replace(/\{on\}/g, l.on ?? l.class_name ?? '').replace(/\{class\}/g, l.class_name ?? '').replace(/\{site\}/g, l.site ?? '');
const aboveOf = (t: Template, s: Style, l?: PrintedLabel) => (s.logo || !s.lineAbove.trim() ? '' : l ? fillTokens(t, l, s.lineAbove) : s.lineAbove);
/** The logo is 60% of the QR's width (the owner, 2026-10-25: "a little smaller"); the height it takes follows its picture. */
const logoW = (t: Template) => t.qr * 0.6;
const isCurved = (s: Style) => s.curve && s.ring && s.shape === 'circle';
/** The logo above the QR: DESIGO (the default) or RTCOM (the owner, 2026-10-26: the barrels carry the RTCOM logo). */
const logoOf = (s: Style) => (s.logoBrand === 'rtcom' ? { src: rtcomLogo, ratio: RTCOM_RATIO } : { src: desigoLogo, ratio: LOGO_RATIO });
const aboveH = (t: Template, s: Style) => (s.logo ? logoW(t) * logoOf(s).ratio + 0.6 : s.lineAbove.trim() ? s.codePt * PT * 1.2 : 0);
/** What is drawn above the QR, in the band that starts at yTop and is `ah` tall: the logo, or the line of words. */
function aboveSvg(t: Template, s: Style, l: PrintedLabel, cx: number, yTop: number, ah: number): string {
  if (s.logo) { const w = logoW(t); const lg = logoOf(s); return `<image href="${lg.src}" x="${f(cx - w / 2)}" y="${f(yTop + 0.25)}" width="${f(w)}" height="${f(w * lg.ratio)}" preserveAspectRatio="xMidYMid meet"/>`; }
  const ab = aboveOf(t, s, l);
  return ab ? `<text x="${f(cx)}" y="${f(yTop + ah * 0.8)}" font-size="${f(s.codePt * PT)}" font-family="sans-serif" font-weight="600" text-anchor="middle">${esc(ab)}</text>` : '';
}
export const isWide = (t: Template) => t.labelW >= t.labelH * 1.4;

// ── The frame's geometry ──────────────────────────────────────────────────────────────────────────────────────
/** A shape of circumradius 1: its sides and the angle of its first corner (y down), chosen so a flat side, or a
 * point for the pentagon and diamond, is at the top. */
const POLY: Record<Exclude<Shape, 'circle'>, { n: number; a0: number }> = {
  square: { n: 4, a0: 45 }, hexagon: { n: 6, a0: 0 }, octagon: { n: 8, a0: 22.5 }, pentagon: { n: 5, a0: -90 }, diamond: { n: 4, a0: -90 },
};
const unitCorners = (sh: Exclude<Shape, 'circle'>) => {
  const { n, a0 } = POLY[sh];
  return Array.from({ length: n }, (_, k) => { const a = ((a0 + (k * 360) / n) * Math.PI) / 180; return [Math.cos(a), Math.sin(a)] as const; });
};
/** For a shape of circumradius 1: its apothem (centre to a side) and the half-extent of its bounding box. */
const unitFacts = (sh: Shape) => {
  if (sh === 'circle') return { apothem: 1, half: 1 };
  const pts = unitCorners(sh);
  return { apothem: Math.cos(Math.PI / POLY[sh].n), half: Math.max(...pts.map(([x, y]) => Math.max(Math.abs(x), Math.abs(y)))) };
};

/** One outline of the frame: the shape at apothem A (distance from the centre to its sides; the radius for a circle). */
interface Outline { d: string; pts: [number, number][] | null; cx: number; cy: number; A: number; shape: Shape }
function outline(shape: Shape, cx: number, cy: number, A: number, corner: number): Outline {
  if (shape === 'circle') {
    return { d: `M ${f(cx - A)} ${f(cy)} A ${f(A)} ${f(A)} 0 1 1 ${f(cx + A)} ${f(cy)} A ${f(A)} ${f(A)} 0 1 1 ${f(cx - A)} ${f(cy)} Z`, pts: null, cx, cy, A, shape };
  }
  const R = A / unitFacts(shape).apothem;
  const pts = unitCorners(shape).map(([x, y]) => [cx + R * x, cy + R * y] as [number, number]);
  return { d: roundedPath(pts, corner), pts, cx, cy, A, shape };
}
/** A closed polygon whose corners are rounded by c millimetres (a quadratic curve through each corner). */
function roundedPath(pts: [number, number][], c: number): string {
  if (c <= 0.01) return `M ${pts.map(([x, y]) => `${f(x)} ${f(y)}`).join(' L ')} Z`;
  const n = pts.length;
  const cut = (p: [number, number], q: [number, number]) => {
    const len = Math.hypot(q[0] - p[0], q[1] - p[1]); const t = Math.min(c, len / 2) / len;
    return [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t] as const;
  };
  let d = '';
  for (let i = 0; i < n; i++) {
    const p = pts[i]!; const prev = pts[(i + n - 1) % n]!; const next = pts[(i + 1) % n]!;
    const a = cut(p, prev); const b = cut(p, next);
    d += `${i ? ' L' : 'M'} ${f(a[0])} ${f(a[1])} Q ${f(p[0])} ${f(p[1])} ${f(b[0])} ${f(b[1])}`;
  }
  return `${d} Z`;
}
/** n points evenly along an outline, clockwise from the top centre, each with the direction of travel in degrees. */
function along(o: Outline, n: number): { x: number; y: number; deg: number }[] {
  if (!o.pts) {
    return Array.from({ length: n }, (_, i) => { const t = (i / n) * Math.PI * 2 - Math.PI / 2;
      return { x: o.cx + o.A * Math.cos(t), y: o.cy + o.A * Math.sin(t), deg: (t * 180) / Math.PI + 90 }; });
  }
  const pts = o.pts; const segs = pts.map((p, i) => { const q = pts[(i + 1) % pts.length]!; return { p, q, len: Math.hypot(q[0] - p[0], q[1] - p[1]) }; });
  const total = segs.reduce((s, x) => s + x.len, 0);
  return Array.from({ length: n }, (_, i) => {
    let d = (i / n) * total;
    for (const s of segs) {
      if (d <= s.len) { const t = d / s.len; return { x: s.p[0] + (s.q[0] - s.p[0]) * t, y: s.p[1] + (s.q[1] - s.p[1]) * t, deg: (Math.atan2(s.q[1] - s.p[1], s.q[0] - s.p[0]) * 180) / Math.PI }; }
      d -= s.len;
    }
    return { x: pts[0]![0], y: pts[0]![1], deg: 0 };
  });
}
const perimeter = (o: Outline) => (o.pts ? o.pts.reduce((s, p, i) => { const q = o.pts![(i + 1) % o.pts!.length]!; return s + Math.hypot(q[0] - p[0], q[1] - p[1]); }, 0) : 2 * Math.PI * o.A);

/** The frame's size: the outer apothem that keeps the whole frame inside a D × D square. */
const outerApothem = (shape: Shape, D: number) => { const u = unitFacts(shape); return (D / 2) * (u.apothem / u.half); };

/** The frame's middle line (where text sits) and its band thickness. */
function frameGeom(t: Template, s: Style) {
  const D = Math.min(s.ringD, t.labelW, t.labelH); const w = Math.min(s.ringW, D / 4);
  const A = outerApothem(s.shape, D) - w / 2;
  return { D, w, A };
}
/** Text on the frame is made to fit its line: a size that leaves the letters inside the arc, never below 1.2 mm.
 *  The width is an estimate (about 0.62 of the size per letter, plus the letter gap). */
function frameTextFit(t: Template, s: Style, str: string, part: 'top' | 'bottom' | 'around', row: 0 | 1 | 2 | null = null) {
  const { w, A } = frameGeom(t, s);
  // Two lines round the frame (the owner, 2026-10-26): each takes under half the band, and may be drawn down to 0.8 mm.
  const two = row !== null && !!s.frameAround2.trim();
  const wanted = Math.min(s.framePt * PT, w * (two ? (tallOf(str) ? 0.26 : 0.4) : 0.75));
  const shift = two ? rowShift(w, wanted, row!, tallOf(str)) : part === 'bottom' ? wanted * 0.35 : -wanted * 0.35;
  const o = outline(s.shape, 0, 0, A + shift, 0);
  const room = (part === 'around' ? perimeter(o) * 0.96 : perimeter(o) / 2 * 0.92);
  const n = Math.max(1, str.length);
  const em = two ? 0.56 : 0.62;   // measured in Edge: bold 5 mm text runs about 0.5 em a letter, plus the 0.15 gap
  const width = (size: number) => n * size * em + (n - 1) * 0.15;
  const floor = two ? 0.8 : 1.2;
  const size = width(wanted) <= room ? wanted : Math.max(floor, (room - (n - 1) * 0.15) / (n * em));
  return { size, tooLong: width(size) > room + 0.01, room, wanted, used: Math.min(width(size), room) };
}
/** Where a baseline of lines round the frame sits relative to the band's middle: the outer line (0) just above it, the inner line (1) just below it (both close, the band's middle between them);
 *  row 2 is the outer line along the bottom (its letters hang inwards). */
const ROW_GAP = 0.35;
/** Devanagari letters stand taller (the head line above, the matras below) than Latin capitals: such a line gets more height and gap. */
const tallOf = (str: string) => /[ऀ-ॿ]/.test(str);
const rowShift = (_w: number, fs: number, row: 0 | 1 | 2, tall = false) => {
  const h = (tall ? 1.1 : 0.72) * fs; const gap = ROW_GAP + (tall ? 0.3 * fs : 0);
  return row === 0 ? gap / 2 : row === 1 ? -(h + gap / 2) : gap / 2 + h;
};
/** Words for what the frame's text cannot do: it is too long for its line, or made smaller to fit. */
export function frameTextAdvice(t: Template): string[] {
  const s = styleOf(t);
  if (!s.ring) return [];
  const a: string[] = [];
  const texts: ['top' | 'bottom' | 'around', string][] = s.frameMode === 'around' ? [['around', s.frameAround], ['around', s.frameAround2], ...(s.frameAround2.trim() ? [['bottom', s.frameBottom] as ['bottom', string]] : [])] : [['top', s.frameTop], ['bottom', s.frameBottom]];
  for (const [part, str] of texts) {
    if (!str.trim()) continue;
    const x = frameTextFit(t, s, str.replace(/\{code\}/g, 'XXXXXXXXXXXX'), part, part === 'around' ? (str === s.frameAround ? 0 : 1) : s.frameMode === 'around' ? 2 : null);
    const where = part === 'around' ? 'all round' : part;
    if (x.tooLong) a.push(`The ${where} text is too long for the frame (room for about ${Math.floor(x.room / (1.2 * 0.62 + 0.15))} letters at the smallest size): shorten it or make the frame bigger.`);
    else if (x.size < x.wanted - 0.01) a.push(`The ${where} text is drawn smaller (${(x.size / PT).toFixed(1)} pt) to fit the frame.`);
  }
  return a;
}

/** What does not fit a frame. */
export function ringProblems(t: Template): string[] {
  const s = styleOf(t);
  if (!s.ring) return [];
  const p: string[] = [];
  if (s.ringD > Math.min(t.labelW, t.labelH) + 0.05) p.push('The frame is bigger than the label.');
  if (s.ringW * 2 >= outerApothem(s.shape, s.ringD) * 1.2) p.push('The frame is too thick for its size.');
  return p;
}

/** Inside a frame the QR (with the text under it) is made to fit: the largest size up to the chosen one whose corners
 * stay inside the frame (the owner, 2026-10-03: a 42 mm QR in a 50 mm ring ran over it). */
export function ringFit(t: Template): { qr: number; shrunk: boolean } {
  const s = styleOf(t);
  if (!s.ring) return { qr: t.qr, shrunk: false };
  const D = Math.min(s.ringD, t.labelW, t.labelH); const w = Math.min(s.ringW, D / 4);
  const Ain = outerApothem(s.shape, D) - w - 0.6;                 // a little air inside the frame
  const onFrame = codeOnFrame(t, s);
  const codeH = t.showCode && !onFrame && !isCurved(s) ? s.codePt * PT * 1.2 : 0;
  const h = aboveH(t, s) + codeH + ([t.showOn, t.showSite].filter(Boolean).length + extraLines(t).length) * t.textPt * PT * 1.2;
  let fit: number;
  if (isCurved(s)) {
    // the QR sits on the circle's centre, the logo above it, the code curved under it: the QR's corners and the logo's top corners stay inside
    fit = Math.min(Math.SQRT2 * Ain, 2 * (Math.sqrt(Math.max(0, Ain * Ain - (logoW(t) / 2) ** 2)) - aboveH(t, s)));
  } else if (s.shape === 'square') {
    const a = Ain - Math.max(0, s.corner - w) * (1 - Math.SQRT1_2);   // a rounded corner takes a little of the square
    fit = 2 * a - h;
  } else {
    // A circle, or the circle inside a polygon's sides: q² + (q + h)² = (2R)²
    fit = (-h + Math.sqrt(Math.max(0, 8 * Ain * Ain - h * h))) / 2;
  }
  fit = Math.max(4, fit);
  return fit < t.qr - 0.05 ? { qr: Math.floor(fit * 10) / 10, shrunk: true } : { qr: t.qr, shrunk: false };
}

/** The frame itself: centre (cx, cy), its middle line at apothem A, thickness w. */
function frame(d: Design, shape: Shape, corner: number, cx: number, cy: number, A: number, w: number, c: string, a: string, id: string): { defs: string; body: string } {
  const at = (AA: number) => outline(shape, cx, cy, AA, Math.max(0, corner + (AA - A)));
  const line = (AA: number, sw: number, extra = '', stroke = c) => `<path d="${at(AA).d}" fill="none" stroke="${stroke}" stroke-width="${f(sw)}" stroke-linejoin="round" ${extra}/>`;
  const mid = at(A);
  const around = (n: number, draw: (x: number, y: number, deg: number, i: number) => string) => along(mid, n).map((p, i) => draw(p.x, p.y, p.deg, i)).join('');
  const count = (size: number) => Math.max(8, Math.round(perimeter(mid) / size));
  switch (d) {
    case 'double': return { defs: '', body: line(A - w / 3, w / 4) + line(A + w / 3, w / 4) };
    case 'dashed': return { defs: '', body: line(A, w, `stroke-dasharray="${f(w * 0.9)} ${f(w * 1.5)}" stroke-linecap="round"`) };
    case 'beads': return { defs: '', body: around(count(w * 1.25), (x, y, _d, i) => `<circle cx="${f(x)}" cy="${f(y)}" r="${f(w * (i % 2 ? 0.32 : 0.48))}" fill="${i % 2 ? a : c}"/>`) };
    case 'ticks': return { defs: '', body: line(A + w * 0.3, w * 0.25) + around(60, (x, y, deg, i) =>
      `<rect x="${f(x - w * 0.06)}" y="${f(y - w * (i % 5 ? 0.25 : 0.45))}" width="${f(w * 0.12)}" height="${f(w * (i % 5 ? 0.5 : 0.9))}" fill="${c}" transform="rotate(${f(deg)} ${f(x)} ${f(y)})"/>`) };
    case 'drops': return { defs: '', body: line(A, w * 0.22) + around(count(w * 2.2), (x, y, deg) =>
      `<path d="M0 ${f(-w * 0.5)} C ${f(w * 0.32)} ${f(-w * 0.1)} ${f(w * 0.32)} ${f(w * 0.36)} 0 ${f(w * 0.36)} C ${f(-w * 0.32)} ${f(w * 0.36)} ${f(-w * 0.32)} ${f(-w * 0.1)} 0 ${f(-w * 0.5)} Z" fill="${a}" stroke="${c}" stroke-width="${f(w * 0.06)}" transform="translate(${f(x)} ${f(y)}) rotate(${f(deg)})"/>`) };
    case 'leaves': return { defs: '', body: line(A, w * 0.18) + around(count(w * 1.6), (x, y, deg, i) =>
      `<ellipse cx="0" cy="0" rx="${f(w * 0.22)}" ry="${f(w * 0.5)}" fill="${i % 2 ? a : c}" transform="translate(${f(x)} ${f(y)}) rotate(${f(deg + (i % 2 ? 40 : -40))})"/>`) };
    case 'stars': {
      const star = (s: number) => Array.from({ length: 10 }, (_, k) => { const rr = k % 2 ? s * 0.42 : s; const t = (k / 10) * Math.PI * 2 - Math.PI / 2; return `${f(rr * Math.cos(t))},${f(rr * Math.sin(t))}`; }).join(' ');
      return { defs: '', body: line(A, w, `stroke-opacity="0.25"`) + around(count(w * 1.5), (x, y, deg, i) => `<polygon points="${star(w * 0.42)}" fill="${i % 3 ? c : a}" transform="translate(${f(x)} ${f(y)}) rotate(${f(deg)})"/>`) };
    }
    case 'sheen': return {
      defs: `<linearGradient id="${id}g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${c}"/><stop offset="1" stop-color="${a}"/></linearGradient>`,
      body: line(A, w, '', `url(#${id}g)`) + line(A + w * 0.28, w * 0.12, 'stroke-opacity="0.35"', '#fff') };
    case 'emboss': return {
      defs: `<linearGradient id="${id}e" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity="0.55"/><stop offset="0.5" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity="0.35"/></linearGradient>`,
      body: line(A, w) + line(A, w, '', `url(#${id}e)`) + line(A - w / 2, w * 0.12, 'stroke-opacity="0.18"', '#000') };
    default: return { defs: '', body: line(A, w) };
  }
}

/** Half of an outline from its left-most middle to its right-most middle, over the top (clockwise) or under the bottom
 * (anticlockwise), so text on it reads left to right; or the whole outline, clockwise from the left, for text all round. */
function textPathD(o: Outline, part: 'top' | 'bottom' | 'around'): { d: string; centre: number } {
  const { cx, cy } = o;
  if (!o.pts) {
    const r = o.A;
    if (part === 'top') return { d: `M ${f(cx - r)} ${f(cy)} A ${f(r)} ${f(r)} 0 0 1 ${f(cx + r)} ${f(cy)}`, centre: 0.5 };
    if (part === 'bottom') return { d: `M ${f(cx - r)} ${f(cy)} A ${f(r)} ${f(r)} 0 0 0 ${f(cx + r)} ${f(cy)}`, centre: 0.5 };
    // From the bottom, clockwise: the top is the middle of the path, so a line longer than half the circle is centred on the top instead of being cut at its start.
    return { d: `M ${f(cx)} ${f(cy + r)} A ${f(r)} ${f(r)} 0 1 1 ${f(cx)} ${f(cy - r)} A ${f(r)} ${f(r)} 0 1 1 ${f(cx)} ${f(cy + r)}`, centre: 0.5 };
  }
  // The outline's points where it crosses the horizontal line through the centre, and the corners between them.
  const pts = o.pts; const n = pts.length;
  const cross: { i: number; x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const p = pts[i]!; const q = pts[(i + 1) % n]!;
    if ((p[1] - cy) * (q[1] - cy) <= 0 && p[1] !== q[1]) { const t = (cy - p[1]) / (q[1] - p[1]); cross.push({ i, x: p[0] + (q[0] - p[0]) * t, y: cy }); }
    else if (p[1] === cy && q[1] !== cy) cross.push({ i, x: p[0], y: cy });
  }
  const left = cross.reduce((m, c) => (c.x < m.x ? c : m)); const right = cross.reduce((m, c) => (c.x > m.x ? c : m));
  // The corners above the centre line, left to right over the top (screen angles −180°…0°), and below it, left to
  // right under the bottom (180°…0°); a corner on the line itself is a crossing already.
  const ang = (p: [number, number]) => (Math.atan2(p[1] - cy, p[0] - cx) * 180) / Math.PI;
  const above = pts.filter((p) => p[1] < cy - 0.001).sort((p, q) => ang(p) - ang(q));
  const below = pts.filter((p) => p[1] > cy + 0.001).sort((p, q) => ang(q) - ang(p));
  const top: [number, number][] = [[left.x, cy], ...above, [right.x, cy]];
  const bottom: [number, number][] = [[left.x, cy], ...below, [right.x, cy]];
  if (part === 'top') return { d: smoothOpen(top), centre: 0.5 };
  if (part === 'bottom') return { d: smoothOpen(bottom), centre: 0.5 };
  // All round: from the left middle over the top and back under the bottom; the top centre is half the top's length in.
  const lt = lineLength(top); const lb = lineLength(bottom);
  return { d: smoothOpen([...top, ...[...bottom].reverse().slice(1)]), centre: lt / 2 / (lt + lb) };
}
/** An open line through the points whose corners are rounded wide, so letters on it bend gently instead of breaking
 * at a corner. */
function smoothOpen(ps: [number, number][]): string {
  let d = `M ${f(ps[0]![0])} ${f(ps[0]![1])}`;
  for (let i = 1; i < ps.length - 1; i++) {
    const p = ps[i]!; const a = ps[i - 1]!; const b = ps[i + 1]!;
    const la = Math.hypot(a[0] - p[0], a[1] - p[1]); const lb = Math.hypot(b[0] - p[0], b[1] - p[1]);
    const c = 0.45 * Math.min(la, lb);
    const pa = [p[0] + ((a[0] - p[0]) * c) / la, p[1] + ((a[1] - p[1]) * c) / la]; const pb = [p[0] + ((b[0] - p[0]) * c) / lb, p[1] + ((b[1] - p[1]) * c) / lb];
    d += ` L ${f(pa[0]!)} ${f(pa[1]!)} Q ${f(p[0])} ${f(p[1])} ${f(pb[0]!)} ${f(pb[1]!)}`;
  }
  const z = ps[ps.length - 1]!;
  return `${d} L ${f(z[0])} ${f(z[1])}`;
}
const lineLength = (ps: [number, number][]) => ps.slice(1).reduce((s, p, i) => s + Math.hypot(p[0] - ps[i]![0], p[1] - ps[i]![1]), 0);

/** One label, drawn at (x, y) in millimetres. `qrSvg` is the QR as the qrcode library made it; `image` an uploaded picture. */
export function labelSvg(t: Template, l: PrintedLabel, qrSvg: string, x: number, y: number, id: string, image?: string | null): string {
  const s = styleOf(t);
  const text = textLines(t, l);
  const sizeOf = (i: number) => (i === 0 && t.showCode ? s.codePt : t.textPt);
  const fam = (i: number) => (i === 0 && t.showCode ? 'monospace' : 'sans-serif');
  const weight = (i: number) => (i === 0 && t.showCode ? ' font-weight="600"' : '');
  const border = t.border ? `<rect x="${f(x)}" y="${f(y)}" width="${f(t.labelW)}" height="${f(t.labelH)}" fill="none" stroke="#888" stroke-width="0.2"/>` : '';
  // Transparent: the QR's own white square goes, only its dark modules are drawn.
  const fillIn = /^#[0-9a-f]{6}$/i.test(s.insideColor) ? s.insideColor : '#ffffff';
  const qrBody = s.transparent ? qrSvg.replace(/<path[^>]*fill="#ffffff"[^>]*\/>/i, '') : fillIn === '#ffffff' ? qrSvg : qrSvg.replace(/(<path[^>]*fill=")#ffffff("[^>]*\/>)/i, `$1${fillIn}$2`);
  const qrAt = (qx: number, qy: number, size = t.qr) => qrBody.replace('<svg ', `<svg x="${f(qx)}" y="${f(qy)}" width="${f(size)}" height="${f(size)}" `);

  if (!s.ring) {
    if (isWide(t)) {
      const qx = x + 1.5; const qy = y + (t.labelH - t.qr) / 2;
      const heights = text.map((_, i) => sizeOf(i) * PT * 1.2); const total = heights.reduce((a, b) => a + b, 0);
      let ty = y + t.labelH / 2 - total / 2;
      const txt = text.map((str, i) => { ty += heights[i]!; return `<text x="${f(x + 3 + t.qr)}" y="${f(ty - heights[i]! * 0.2)}" font-size="${f(sizeOf(i) * PT)}" font-family="${fam(i)}"${weight(i)}>${esc(str)}</text>`; }).join('');
      return border + qrAt(qx, qy) + txt;
    }
    const ah = aboveH(t, s);
    const qx = x + (t.labelW - t.qr) / 2; const qy = y + 1 + ah;
    const above = aboveSvg(t, s, l, x + t.labelW / 2, y + 1, ah);
    let ty = qy + t.qr;
    const txt = text.map((str, i) => { const h = sizeOf(i) * PT * 1.2; ty += h; return `<text x="${f(x + t.labelW / 2)}" y="${f(ty - h * 0.2)}" font-size="${f(sizeOf(i) * PT)}" font-family="${fam(i)}"${weight(i)} text-anchor="middle">${esc(str)}</text>`; }).join('');
    return border + above + qrAt(qx, qy) + txt;
  }

  // With a frame: centred on the label; the QR (and the text, unless the code is on the frame) inside it.
  const D = Math.min(s.ringD, t.labelW, t.labelH); const w = Math.min(s.ringW, D / 4);
  const cx = x + t.labelW / 2; const cy = y + t.labelH / 2;
  const Aout = outerApothem(s.shape, D); const A = Aout - w / 2; const Ain = Aout - w;
  const corner = s.shape === 'circle' ? 0 : Math.max(0, s.corner);
  const onFrame = codeOnFrame(t, s);
  const bend = isCurved(s);
  const inside = bend ? [] : onFrame && t.showCode ? text.slice(1) : text;
  const first = onFrame && t.showCode ? 1 : 0;
  const insideSizes = inside.map((_, k) => sizeOf(k + first));
  const textH = insideSizes.reduce((a, p) => a + p * PT * 1.2, 0);
  const q = ringFit(t).qr;
  const ah = aboveH(t, s);
  const qx = cx - q / 2; const qy = bend ? cy - q / 2 : cy - (ah + q + textH) / 2 + ah; const top0 = qy - ah;   // a curved code leaves the QR on the circle's centre
  const defs: string[] = []; const back: string[] = [];
  const clip = `${id}c`;
  const innerD = outline(s.shape, cx, cy, Ain, Math.max(0, corner - w / 2)).d;
  defs.push(`<clipPath id="${clip}"><path d="${innerD}"/></clipPath>`);
  if (s.bg === 'glow') {
    defs.push(`<radialGradient id="${id}r"><stop offset="0.45" stop-color="#fff"/><stop offset="1" stop-color="${s.glowColor}"/></radialGradient>`);
    back.push(`<path d="${innerD}" fill="url(#${id}r)"/>`);
  } else if (s.bg === 'tint') {
    back.push(`<path d="${innerD}" fill="${s.ringColor}" fill-opacity="0.12"/>`);
  } else if (s.bg === 'image' && image) {
    back.push(`<image href="${image}" x="${f(cx - Ain * 1.5)}" y="${f(cy - Ain * 1.5)}" width="${f(Ain * 3)}" height="${f(Ain * 3)}" preserveAspectRatio="xMidYMid slice" opacity="${s.imageOpacity}" clip-path="url(#${clip})"/>`);
  } else if (!s.transparent) {
    back.push(`<path d="${innerD}" fill="${fillIn}"/>`);
  }
  const rg = frame(s.design, s.shape, corner, cx, cy, A, w, s.ringColor, s.accent, id);
  if (rg.defs) defs.push(rg.defs);
  // The white plate under the QR and its text, so a backdrop never touches the code.
  const plate = s.bg === 'white' ? '' : `<rect x="${f(qx - 0.8)}" y="${f(top0 - 0.8)}" width="${f(q + 1.6)}" height="${f(ah + q + textH + 1.6)}" rx="1.2" fill="${fillIn}" fill-opacity="0.92"/>`;
  // A curved code (the owner, 2026-10-26): the code runs along the bottom of the white; the logo stays straight above the QR.
  const aboveText = aboveSvg(t, s, l, cx, top0, ah); let curvedCode = '';
  if (bend) {
    const code = t.showCode && !onFrame ? codeText(t, l.payload) : '';
    if (code) {
      const r = Ain - 0.5; defs.push(`<path id="${id}cc" d="M ${f(cx - r)} ${f(cy)} A ${f(r)} ${f(r)} 0 0 0 ${f(cx + r)} ${f(cy)}"/>`);
      curvedCode = `<text font-size="${f(s.codePt * PT)}" font-family="monospace" font-weight="600"><textPath href="#${id}cc" startOffset="50%" text-anchor="middle">${esc(code)}</textPath></text>`;
    }
  }
  let ty = qy + q;
  const txt = inside.map((str, k) => { const h = insideSizes[k]! * PT * 1.2; ty += h; const i = k + first;
    return `<text x="${f(cx)}" y="${f(ty - h * 0.2)}" font-size="${f(insideSizes[k]! * PT)}" font-family="${fam(i)}"${weight(i)} text-anchor="middle">${esc(str)}</text>`; }).join('');
  // Text on the frame's line, centred in its band: on the top half the letters stand outwards, on the bottom half
  // inwards, so the baseline sits a little inside or outside the middle line.
  const fill = (str: string) => esc(fillTokens(t, l, str));
  const onLine = (part: 'top' | 'bottom' | 'around', str: string, k: number, row: 0 | 1 | 2 | null = null) => {
    const fit = frameTextFit(t, s, fillTokens(t, l, str), part, row); const fs = fit.size;
    const two = row !== null && !!s.frameAround2.trim();
    const shift = two ? rowShift(w, fs, row!, tallOf(str)) : part === 'bottom' ? fs * 0.35 : -fs * 0.35;
    const o = outline(s.shape, cx, cy, A + shift, 0);
    const tp = textPathD(o, part);
    defs.push(`<path id="${id}t${k}" d="${tp.d}"/>`);
    return `<text font-size="${f(fs)}" font-family="sans-serif" font-weight="700" fill="${row === 1 && s.frameColor2 ? s.frameColor2 : s.frameColor}" letter-spacing="0.15"><textPath href="#${id}t${k}" startOffset="${f(tp.centre * 100)}%" text-anchor="middle">${fill(str)}</textPath></text>`;
  };
  let curved = '';
  if (s.frameMode === 'around') {
    if (s.frameAround.trim()) curved = onLine('around', s.frameAround, 0, 0);
    if (s.frameAround2.trim()) curved += onLine('around', s.frameAround2, 2, 1);
    // with two lines, the bottom line (the website and the phone) runs along the bottom of the outer row, where the first line leaves room
    if (s.frameAround2.trim() && s.frameBottom.trim()) curved += onLine('bottom', s.frameBottom, 3, 2);
  } else {
    if (s.frameTop.trim()) curved += onLine('top', s.frameTop, 0);
    if (s.frameBottom.trim()) curved += onLine('bottom', s.frameBottom, 1);
  }
  return `<g><defs>${defs.join('')}</defs>${border}${back.join('')}${rg.body}${plate}${aboveText}${qrAt(qx, qy, q)}${txt}${curvedCode}${curved}</g>`;
}

/** A small swatch of a design on a shape, for the picker. */
export function designSwatch(d: Design, c: string, a: string, shape: Shape = 'circle', corner = 0): string {
  const A = outerApothem(shape, 20) - 1.3;
  const rg = frame(d, shape, shape === 'circle' ? 0 : corner * 0.4, 12, 12, A, 2.6, c, a, `sw-${d}-${shape}`);
  const inner = outline(shape, 12, 12, A - 1.3, 0).d;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40"><defs>${rg.defs}</defs><path d="${inner}" fill="#fff"/>${rg.body}<rect x="9" y="9" width="6" height="6" fill="#111"/><rect x="10" y="10" width="4" height="4" fill="#fff"/><rect x="11" y="11" width="2" height="2" fill="#111"/></svg>`;
}
/** A small swatch of a shape, for the shape picker. */
export function shapeSwatch(shape: Shape, c: string, corner = 0): string {
  const o = outline(shape, 12, 12, outerApothem(shape, 20) - 1, shape === 'circle' ? 0 : corner * 0.4);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32"><path d="${o.d}" fill="none" stroke="${c}" stroke-width="2" stroke-linejoin="round"/></svg>`;
}
